import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, defer, forkJoin, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { IRackReference, IRackset, IResponseRack, IResponseRackset, RackingService } from '../racking.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Racksets';
const importConcurrency = 5;

const NAME_PATTERN = /^[0-9a-zA-ZÆØÅæøå\s-_]*$/;
const DESCRIPTION_PATTERN = /^[0-9a-zA-ZÆØÅæøå.,\s]*$/;

export interface IRacksetExportData {
	name: string;
	description?: string;
	/** JSON text: the rackset's racks by name, in priority order. See {@link RacksetSchema}. */
	racks?: string;
}

/**
 * `racks` carries JSON text in a plain `'string'` column - the same shape terminalTemplateTransfer uses for its own object lists, and for the same reason: a rackset holds a list of racks, which the Excel schema cannot express (ExcelFileTransfer rejects any array of non-primitives).
 *
 * The list is written as rack NAMES rather than `self` URLs, since a name is the only part of a rack a user can read or type, and its ORDER is the priority - the list page rewrites every priority from its row position on each reorder, so position is the only thing that was ever meaningful.
 */
const RacksetSchema: IFileDataSchema<IRacksetExportData> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name', width: 30 },
		description: { type: 'string', required: false, fileKey: 'Description', width: 50 },
		racks: { type: 'string', required: false, fileKey: 'Racks', width: 60 },
	},
};

/** A row that passed validation, carrying its file location through to a save failure too. */
interface RacksetImportCandidate {
	rackset: IRackset;
	location?: string;
}

interface RacksetImportContext {
	racksetNameMap: Map<string, IResponseRackset>;
	rackSelfByName: Map<string, string>;
}

@Injectable({
	providedIn: 'root',
})
export class RacksetTransferService implements ITransferService<IResponseRackset> {
	private translationPrefix = 'masterdata.racking.racksetImportExport';

	constructor(
		private rackingService: RackingService,
		private excelFileTransfer: ExcelFileTransfer<IRacksetExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// A rackset's racks travel by name rather than by self, so a name has to identify exactly one rack
	// and one rackset on the way back in. Neither the dialog nor this list page enforces that, so both
	// rosters are checked here instead of assumed.
	public exportData(racksets: IResponseRackset[]): Observable<IResponseRackset[]> {
		const duplicateNames = findDuplicateNames(racksets);
		if (duplicateNames.length > 0) {
			return throwError(
				() =>
					new Error(
						this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, {
							names: duplicateNames.join(', '),
						}),
					),
			);
		}

		return this.rackingService.getRacks().pipe(
			mergeMap((racks) => {
				const nameBySelf = new Map(racks.items.map((rack) => [rack.self, rack.name]));
				const references = racksets.flatMap((rackset) => rackset.racks ?? []);

				const unknownRacks = references.filter((reference) => !nameBySelf.has(reference.rack?.self)).map((reference) => reference.rack?.self);
				if (unknownRacks.length > 0) {
					return throwError(
						() =>
							new Error(
								this.translator.instant(`${this.translationPrefix}.export.errors.unknown-racks.content`, {
									racks: Array.from(new Set(unknownRacks)).join(', '),
								}),
							),
					);
				}

				const { ambiguousNames, racksetsWithRepeats } = findRackReferenceProblems(racksets, nameBySelf);
				if (ambiguousNames.length > 0) {
					return throwError(
						() =>
							new Error(
								this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-rack-names.content`, {
									names: ambiguousNames.join(', '),
								}),
							),
					);
				}
				if (racksetsWithRepeats.length > 0) {
					return throwError(
						() =>
							new Error(
								this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-rack-in-rackset.content`, {
									names: racksetsWithRepeats.join(', '),
								}),
							),
					);
				}

				const data = racksets.map((rackset) => toExportRow(rackset, nameBySelf));

				return defer(() => this.excelFileTransfer.exportData(sheetName, data, RacksetSchema)).pipe(
					map(() => racksets),
					catchError((error) => {
						throw new Error(`Failed to export data: ${error.message}`);
					}),
				);
			}),
		);
	}

	public downloadTemplate(): void {
		const exampleRow: IRacksetExportData = {
			name: 'Example Rackset',
			description: 'Example description',
			racks: JSON.stringify(['Example Rack', 'Another Rack']),
		};

		this.excelFileTransfer.exportData(`${sheetName}Template`, [exampleRow], RacksetSchema).subscribe();
	}

	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed - whatever went wrong is already in ImportErrors. Stop here instead of
				// fetching the rosters only to report an unrelated duplicate-name error that would bury
				// the actual parse failure.
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return forkJoin({
					racksets: this.rackingService.getRacksets(),
					racks: this.rackingService.getRacks(),
				}).pipe(
					mergeMap(({ racksets, racks }) => {
						const rosterDuplicates = findDuplicateNames(racksets.items);
						// A racks cell addresses a rack by name, so two racks sharing one leaves every
						// mention of it ambiguous. Only the names this file actually uses matter: the rack
						// roster is a lookup table here, not the entity being imported, so a duplicate pair
						// no row mentions is none of this import's business.
						const referencedRackNames = collectReferencedRackNames(parsedRows);
						const rackDuplicates = findDuplicateNames(racks.items).filter((name) => referencedRackNames.has(normalizeName(name)));

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						if (rackDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-rack-names-in-roster', rackDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0 || rackDuplicates.length > 0;

						const ctx: RacksetImportContext = {
							racksetNameMap: buildRacksetNameMap(racksets.items),
							rackSelfByName: buildRackSelfByName(racks.items),
						};

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ImportErrors, ctx)),
							toArray(),
							mergeMap((validRows) => (namesAreAmbiguous ? of([]) : from(validRows).pipe(mergeMap(this.createOrUpdateRackset(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<IRacksetExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, RacksetSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Mirrors the validators the create/edit dialog applies to its form (the name and description
	// patterns and lengths), since import is a second, dialog-bypassing entry point into the same data -
	// plus the rules the dialog enforces by construction rather than by validator: its rack picker is a
	// multi-select over existing racks, so it can neither offer an unknown rack nor the same rack twice.
	private validateRow(ImportErrors: ImportErrorTypes[], ctx: RacksetImportContext) {
		return (imported: IImportedData<IRacksetExportData>): RacksetImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			const name = (row.name ?? '').trim();
			if (!name) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-name`),
				};
			} else if (name.length > 50) {
				errors['name'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.name-too-long`),
				};
			} else if (!NAME_PATTERN.test(name)) {
				errors['name'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.name-invalid-characters`),
				};
			}

			const description = (row.description ?? '').trim();
			if (description.length > 100) {
				errors['description'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.description-too-long`),
				};
			} else if (description && !DESCRIPTION_PATTERN.test(description)) {
				errors['description'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.description-invalid-characters`),
				};
			}

			const racks = this.parseRacks(row.racks, ctx, errors);

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: name || '',
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			const rackset: IRackset = {
				self: null,
				name,
				description: description || '',
				racks: new MatTableDataSource<IRackReference>(racks),
				selectedRacks: racks.map((reference) => reference.rack.self),
				updatedDate: null,
				updatedBy: null,
			};

			return { rackset, location };
		};
	}

	private parseRacks(raw: string | undefined, ctx: RacksetImportContext, errors: Record<string, ValidationErrors>): IRackReference[] {
		const cell = (raw ?? '').trim();
		if (!cell) {
			return [];
		}

		let parsed: unknown;
		try {
			parsed = JSON.parse(cell);
		} catch {
			errors['racks'] = {
				invalidJson: this.translator.instant(`${this.translationPrefix}.import.errors.racks-invalid-json`),
			};
			return [];
		}

		if (!Array.isArray(parsed)) {
			errors['racks'] = {
				invalidShape: this.translator.instant(`${this.translationPrefix}.import.errors.racks-not-a-list`),
			};
			return [];
		}

		const names = parsed.map((entry) => (typeof entry === 'string' ? entry.trim() : ''));
		if (names.some((entry) => !entry)) {
			errors['racks'] = {
				invalidEntry: this.translator.instant(`${this.translationPrefix}.import.errors.racks-invalid-entry`),
			};
			return [];
		}

		const duplicates = findDuplicates(names.map((entry) => normalizeName(entry)));
		if (duplicates.length > 0) {
			errors['racks'] = {
				duplicate: this.translator.instant(`${this.translationPrefix}.import.errors.duplicate-rack-in-rackset`, {
					names: names.filter((entry) => duplicates.includes(normalizeName(entry))).join(', '),
				}),
			};
			return [];
		}

		const unknown = names.filter((entry) => !ctx.rackSelfByName.has(normalizeName(entry)));
		if (unknown.length > 0) {
			errors['racks'] = {
				unknown: this.translator.instant(`${this.translationPrefix}.import.errors.unknown-rack-reference`, {
					names: unknown.join(', '),
				}),
			};
			return [];
		}

		// Position is the priority, renumbered from 1 the way the list page does on every reorder.
		return names.map((entry, index) => ({ rack: { self: ctx.rackSelfByName.get(normalizeName(entry)) }, priority: index + 1 }));
	}

	private createOrUpdateRackset(ctx: RacksetImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: RacksetImportCandidate) => {
			const { rackset, location } = candidate;
			// Safe: importData() has already confirmed neither the file nor the roster has a name
			// collision, so at most one existing rackset can match here.
			const existingRackset = ctx.racksetNameMap.get(normalizeName(rackset.name));

			if (existingRackset) {
				rackset.self = existingRackset.self;
				return this.rackingService.updateRackset(rackset).pipe(
					map(() => ({ entity: rackset, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, rackset, location, error);
						return of();
					}),
				);
			}

			return this.rackingService.createRackset(rackset).pipe(
				map(() => ({ entity: rackset, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, rackset, location, error);
					return of();
				}),
			);
		};
	}

	// Never surface the backend's raw error text - map it to a translated, user-facing message instead.
	// Anything we haven't special-cased falls back to a generic "failed to save" message.
	private saveFailedErrorKey(error: unknown): string {
		switch ((error as HttpErrorResponse)?.status) {
			case 400:
				return 'save-failed-bad-request';
			case 404:
				return 'save-failed-not-found';
			default:
				return 'save-failed';
		}
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], rackset: IRackset, location: string | undefined, error: unknown) {
		ImportErrors.push({
			Id: rackset.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${this.saveFailedErrorKey(error)}`, {
				name: rackset.name,
			}),
		});
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster' | 'duplicate-rack-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}
}

/** Racks by name in priority order, as JSON text. A rackset with no racks writes `[]` rather than an empty cell, so the column reads as a list everywhere it is filled in. */
function toExportRow(rackset: IResponseRackset, nameBySelf: Map<string, string>): IRacksetExportData {
	const racks = [...(rackset.racks ?? [])].map((reference, index) => ({ reference, priority: reference.priority ?? index + 1 })).sort((a, b) => a.priority - b.priority);

	return {
		name: rackset.name,
		description: rackset.description || '',
		racks: JSON.stringify(racks.map(({ reference }) => nameBySelf.get(reference.rack?.self))),
	};
}

/** Every rack name any row's racks cell mentions. Unparseable cells are skipped: this only decides which roster duplicates matter, and the row itself reports its own parse failure. */
function collectReferencedRackNames(rows: Array<IImportedData<IRacksetExportData>>): Set<string> {
	const names = new Set<string>();

	rows.forEach((imported) => {
		const cell = (imported.data.racks ?? '').trim();
		if (!cell) {
			return;
		}

		try {
			const parsed: unknown = JSON.parse(cell);
			if (Array.isArray(parsed)) {
				parsed.forEach((entry) => typeof entry === 'string' && names.add(normalizeName(entry)));
			}
		} catch {
			// Reported per row by parseRacks; nothing to collect from a cell that will not parse.
		}
	});

	return names;
}

/** Referenced racks whose name is shared with another referenced rack: the file would carry that name twice over, pointing at two different racks. */
/**
 * Two different rack-reference problems that both leave a rackset unable to round-trip through Excel, found in one pass over the same per-rackset reference lists: two DIFFERENT racks sharing a display name (the exported name can no longer say which one it meant, so re-import could silently resolve to the wrong rack),
 * and the SAME rack referenced twice within one rackset (the cell would carry its name twice over, which the importer's own duplicate check then rejects).
 */
function findRackReferenceProblems(racksets: IResponseRackset[], nameBySelf: Map<string, string>): { ambiguousNames: string[]; racksetsWithRepeats: string[] } {
	const byName = new Map<string, { original: string; selfs: Set<string> }>();
	const racksetsWithRepeats = new Set<string>();

	racksets.forEach((rackset) => {
		const selfsSeenInThisRackset = new Set<string>();

		(rackset.racks ?? []).forEach((reference) => {
			const self = reference.rack?.self;
			if (!self) {
				return;
			}

			if (selfsSeenInThisRackset.has(self)) {
				racksetsWithRepeats.add(rackset.name);
			}
			selfsSeenInThisRackset.add(self);

			const original = nameBySelf.get(self);
			const name = normalizeName(original);
			if (!name) {
				return;
			}

			const seen = byName.get(name);
			if (seen) {
				seen.selfs.add(self);
			} else {
				byName.set(name, { original, selfs: new Set([self]) });
			}
		});
	});

	const ambiguousNames = Array.from(byName.values())
		.filter(({ selfs }) => selfs.size > 1)
		.map(({ original }) => original);

	return { ambiguousNames, racksetsWithRepeats: Array.from(racksetsWithRepeats) };
}

function buildRacksetNameMap(racksets: IResponseRackset[]): Map<string, IResponseRackset> {
	const map = new Map<string, IResponseRackset>();
	racksets.forEach((rackset) => map.set(normalizeName(rackset.name), rackset));
	return map;
}

function buildRackSelfByName(racks: IResponseRack[]): Map<string, string> {
	const map = new Map<string, string>();
	racks.forEach((rack) => map.set(normalizeName(rack.name), rack.self));
	return map;
}

/** Values appearing 2+ times, each reported once. */
function findDuplicates(values: string[]): string[] {
	const seen = new Set<string>();
	const duplicates = new Set<string>();

	values.forEach((value) => {
		if (seen.has(value)) {
			duplicates.add(value);
		} else {
			seen.add(value);
		}
	});

	return Array.from(duplicates);
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
